import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ArtifactTable from "./ArtifactTable";
import { CompromisosServices } from "./BriefcaseServices";
import { fetchCompromisos } from "../../redux";
import planningImg from "../../assets/planning-1.png";

// Compara el VALOR (etiqueta visible) de un estado del DDL contra "cumplido", ignorando
// tildes/mayúsculas — evita depender del ID interno (no versionado, vive solo en BD).
const normalize = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// Matriz de Compromisos (RF-7, contrato ratificado 2026-06-18). Configura columnas/campos y delega a
// ArtifactTable. El selector de Proyecto se puebla con los proyectos ya asociados al portafolio (prop
// `proyectos`, del detalle cargado en EditBriefcase) — sin endpoint nuevo. Criticidad/Estado/Responsable/
// Líder se pueblan con SP_DDL_PORTAFOLIO_COMPROMISOS (scope por organización en backend). Para Estado se
// envía el ID exacto del DDL (valor de BD sin tilde); el VALOR del DDL es la etiqueta visible.
const CompromisosBriefcase = ({ idPortafolio, proyectos = [] }) => {
  const slice = useSelector((state) => state.briefcaseArtifacts.compromisos);
  const [ddl, setDdl] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (idPortafolio) {
      CompromisosServices.getCompromisosDdl(idPortafolio)
        .then((data) => {
          if (!cancelled) setDdl(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (!cancelled) setDdl([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [idPortafolio]);

  // Opciones dinámicas para los selects ({ value, label } con etiqueta literal).
  const dynamicOptions = useMemo(() => {
    const byDdl = (tipo) =>
      ddl
        .filter((r) => r.DDL === tipo)
        .map((r) => ({ value: r.ID, label: r.VALOR }));
    return {
      proyectos: (proyectos || []).map((p) => ({
        value: p.IdProyecto,
        label: p.Nombre,
      })),
      criticidad: byDdl("CRITICIDAD"),
      estado: byDdl("ESTADO_COMPROMISO"),
      responsable: byDdl("RESPONSABLE_COMPROMISO"),
      lider: byDdl("LIDER"),
    };
  }, [ddl, proyectos]);

  // Comparte con requiredIf/hintWhen: true cuando el Estado elegido es "Cumplido".
  const isEstadoCumplido = (formData) => {
    const opt = (dynamicOptions.estado || []).find(
      (o) => String(o.value) === String(formData.Estado)
    );
    return !!opt && normalize(opt.label) === "cumplido";
  };

  const columns = [
    { key: "NombreProyecto", captionKey: "compromisos_col_proyecto", type: "plain", width: 210, align: "center" },
    { key: "Sprint", captionKey: "compromisos_col_sprint", type: "plain", width: 130, align: "center" },
    { key: "Compromiso", captionKey: "compromisos_col_compromiso", type: "plain", width: 340, align: "center" },
    { key: "Criticidad", captionKey: "compromisos_col_criticidad", type: "plain", width: 130, align: "center" },
    { key: "Estado", captionKey: "compromisos_col_estado", type: "plain", width: 160, align: "center" },
    {
      key: "FechaPlaneadaCompletitud",
      captionKey: "compromisos_col_fechaplaneada",
      type: "date",
      width: 160,
      align: "center",
    },
  ];

  const fields = [
    // ── Identificación ──────────────────────────────────────────────────────
    { name: "_sec_id", type: "section", labelKey: "compromisos_section_identificacion", icon: "search" },
    {
      name: "IdProyecto",
      labelKey: "compromisos_label_proyecto",
      type: "select",
      optionsKey: "proyectos",
      placeholderKey: "compromisos_proyecto_select",
      required: true,
      colWidth: 8,
    },
    { name: "Sprint", labelKey: "compromisos_label_sprint", type: "number", colWidth: 4, required: true },
    { name: "Compromiso", labelKey: "compromisos_label_compromiso", type: "textarea", required: true },

    // ── Fechas ──────────────────────────────────────────────────────────────
    { name: "_sec_fechas", type: "section", labelKey: "compromisos_section_fechas", icon: "calendar-alt" },
    {
      name: "FechaCompromiso",
      labelKey: "compromisos_label_fechacompromiso",
      type: "datepicker",
      colWidth: 4,
    },
    {
      name: "FechaPlaneadaCompletitud",
      labelKey: "compromisos_label_fechaplaneada",
      type: "datepicker",
      colWidth: 4,
    },
    {
      name: "FechaCumplimiento",
      labelKey: "compromisos_label_fechacumplimiento",
      type: "datepicker",
      colWidth: 4,
      // Obligatoria solo cuando el Estado seleccionado corresponde a "Cumplido".
      requiredIf: isEstadoCumplido,
      hintWhen: isEstadoCumplido,
      hintKey: "compromisos_hint_fecha_cumplimiento_requerida",
    },

    // ── Clasificación ────────────────────────────────────────────────────────
    { name: "_sec_clas", type: "section", labelKey: "compromisos_section_clasificacion", icon: "flag" },
    {
      name: "Criticidad",
      labelKey: "compromisos_label_criticidad",
      type: "select",
      optionsKey: "criticidad",
      placeholderKey: "compromisos_criticidad_select",
      required: true,
    },
    {
      name: "Estado",
      labelKey: "compromisos_label_estado",
      type: "select",
      optionsKey: "estado",
      placeholderKey: "compromisos_estado_select",
      required: true,
    },

    // ── Equipo responsable ───────────────────────────────────────────────────
    { name: "_sec_equipo", type: "section", labelKey: "compromisos_section_equipo", icon: "users" },
    {
      name: "Lider",
      labelKey: "compromisos_label_lider",
      type: "select",
      optionsKey: "lider",
      placeholderKey: "compromisos_lider_select",
      required: true,
    },
    {
      name: "Responsable",
      labelKey: "compromisos_label_responsable",
      type: "select",
      optionsKey: "responsable",
      placeholderKey: "compromisos_responsable_select",
    },
    {
      name: "Retrospectiva",
      labelKey: "compromisos_label_vienederetro",
      type: "yesno",
      default: 0,
      // El hint es sobre el valor por defecto al crear: no aplica editando un registro existente.
      tooltipKey: "compromisos_help_retrospectiva_default",
      hideTooltipOnEdit: true,
    },
    { name: "Correo", labelKey: "compromisos_label_correo", type: "email" },

    // ── Detalles adicionales ─────────────────────────────────────────────────
    { name: "_sec_det", type: "section", labelKey: "compromisos_section_detalles", icon: "info-circle" },
    { name: "CausaRaiz", labelKey: "compromisos_label_causaraiz", type: "textarea", required: true },
  ];

  const emptyState = {
    image: planningImg,
    titleKey: "compromisos_empty_title",
    descKey: "compromisos_empty_desc",
    btnKey: "compromisos_empty_btn",
  };

  return (
    <ArtifactTable
      idPortafolio={idPortafolio}
      idField="IdCompromiso"
      columns={columns}
      fields={fields}
      service={CompromisosServices}
      items={slice.items}
      loading={slice.loading}
      error={slice.error}
      fetchAction={fetchCompromisos}
      dynamicOptions={dynamicOptions}
      emptyState={emptyState}
    />
  );
};

export default CompromisosBriefcase;
